import { AbstractMapDomain, MapDomainArrayManager, MapObjectForm, LabeledAutocompleteFormInput, MapToolDefaults, MapChangeEvent, MapToolState, MapTool } from 'geovisto';

// Geovisto core
/**
 * This class provides basic implementation of a theme.
 *
 * @author Jiri Hynek
 * @author Jakub Kachlik - advanced color model
 */
class BasicTheme extends AbstractMapDomain {
    /**
     * It initializes the theme.
     */
    constructor() {
        super();
    }
    /**
     * It returns the theme type.
     */
    getName() {
        return "basic";
    }
    /**
     * It returns the preferred base map.
     */
    getBaseMap() {
        return {
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 20,
            maxNativeZoom: 19,
            subdomains: undefined
        };
    }
    /**
     * It returns if the styles preferres inversed dark colors.
     */
    isDark() {
        return false;
    }
    /**
     * It returns theme font
     */
    getFont() {
        return "'Trebuchet MS', sans-serif";
    }
    /**
     * It returns primary, secondary and disabled foreground colors used for text color
     * @return {primary:color, secondary:color2, disabled:color3}
     */
    getForegroundColors() {
        return {
            primary: "white",
            secondary: "black",
            disabled: "rgba(240, 240, 240, 0.8)"
        };
    }
    /**
     * It returns primary, secondary and disabled background colors
     * @return {primary:color, secondary:color, disabled:color}
     */
    getBackgroundColors() {
        return {
            primary: "rgba(55, 71, 79, 0.9)",
            secondary: "rgba(240, 240, 240, 0.8)",
            disabled: "rgba(55, 71, 79, 0.4)"
        };
    }
    /**
     * It returns highlight colors for selected, highlighted and deepasized (not selected or highlighted) objects
     * @return {selected:color, highlight:color, deempasize:color}
     */
    getHighlightColor() {
        return {
            selected: "orange",
            highlight: "yellow",
            deempasize: "#8c8c8c",
        };
    }
    /**
     * It returns color used for highlight hovered item
     * @return {color}
     */
    getHoverColor() {
        return "yellow";
    }
    /**
     * It returns 4 primary colors
     * lineColor – easy visible color with high contrast
     * triadic1,2,3 - 3 triadic colors scheme (triadic not required but colors contrast recommended)
     * @return {monochrom:color, monochromaComplement:color, triadic1:color, triadic2:color, triadic3:color}
     */
    getDataColors() {
        return {
            lineColor: "slateblue",
            triadic1: "#c1c100",
            triadic2: "#c10000",
            triadic3: "#0006a7",
        };
    }
    /**
     * It returns text input colors
     * matchBg,matchFg- colors for match cases autocomplete
     * notMatchBg, notMatchFg- colors for not matching cases autocomplete
     * placeholder- color of placeholder
     * hover- color when hover object
     * @return {matchBg:color, matchFg:color, notMatchBg:color, notMatchFg:color, placeholder:color, hover:color}
     */
    getTextInputColor() {
        return {
            matchBg: "white",
            matchFg: "black",
            notMatchBg: "#e6e6e6",
            notMatchFg: "#999999",
            placeholder: "#d9d9d9",
            hover: "#cccccc"
        };
    }
}

// Geovisto core
/**
 * This class provide functions for using themes.
 *
 * @author Jiri Hynek
 */
class MapThemesManager extends MapDomainArrayManager {
    constructor(themes) {
        super(themes);
    }
    /**
     * The function returns the default theme.
     */
    getDefault() {
        const objects = this.getDomains();
        return objects && objects.length > 0 ? objects[0] : undefined;
    }
}

/**
 * This class defines a custom theme.
 *
 * @author Jiri Hynek
 * @author Jakub Kachlik - advanced color model
 */
class Dark1Theme extends BasicTheme {
    /**
     * It initializes the dark theme.
     */
    constructor() {
        super();
    }
    /**
     * It returns the theme type.
     */
    getName() {
        return "dark1";
    }
    /**
     * This theme prefers dark colors.
     */
    isDark() {
        return true;
    }
    /**
     * It returns the preferred base map.
     */
    getBaseMap() {
        return {
            url: 'https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        };
    }
    /**
     * It returns theme font
     */
    getFont() {
        return "'Courier New', monospace";
    }
    /**
     * It returns primary, secondary and disabled foreground colors used for text color
     * @return {primary:color, secondary:color2, disabled:color3}
     */
    getForegroundColors() {
        return {
            primary: "#808080",
            secondary: "#b3b3b3",
            disabled: "#cccccc"
        };
    }
    /**
     * It returns primary, secondary and disabled background colors
     * @return {primary:color, secondary:color, disabled:color}
     */
    getBackgroundColors() {
        return {
            primary: "#1a1a1a",
            secondary: "#666666",
            disabled: "#f2f2f2"
        };
    }
    /**
     * It returns color used for highlight hovered item
     * @return {color}
     */
    getHoverColor() {
        return "#690000";
    }
    /**
     * It returns 4 primary colors
     * monochrom - color used as color scale by changing its intensity
     * lineColor – easy visible color with high contrast
     * triadic1,2,3 - 3 triadic colors scheme (triadic not required but colors contrast recommended)
     * @return {monochrom:color, monochromaComplement:color, triadic1:color, triadic2:color, triadic3:color}
     */
    getDataColors() {
        return {
            lineColor: "#c188dd",
            triadic1: "#739550",
            triadic2: "#c99c9c",
            triadic3: "#ba9cc9",
        };
    }
    /**
     * It returns text input colors
     * matchBg,matchFg- colors for match cases autocomplete
     * notMatchBg, notMatchFg- colors for not matching cases autocomplete
     * placeholder- color of placeholder
     * hover- color when hover object
     * @return {matchBg:color, matchFg:color, notMatchBg:color, notMatchFg:color, placeholder:color, hover:color}
     */
    getTextInputColor() {
        return {
            matchBg: "#8c8c8c",
            matchFg: "#d9d9d9",
            notMatchBg: "#666666",
            notMatchFg: "#b3b3b3",
            placeholder: "#bfbfbf",
            hover: "rgb(0, 51, 0, 0.3)"
        };
    }
}

/**
 * This class defines a custom theme.
 *
 * @author Jiri Hynek
 * @author Jakub Kachlik - advanced color model
 */
class Dark2Theme extends BasicTheme {
    /**
     * It initializes the dark theme.
     */
    constructor() {
        super();
    }
    /**
     * It returns the theme type.
     */
    getName() {
        return "dark2";
    }
    /**
     * This theme prefers dark colors.
     */
    isDark() {
        return true;
    }
    /**
     * It returns the preferred base map.
     */
    getBaseMap() {
        return {
            url: 'https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        };
    }
    /**
         * It returns theme font
         */
    getFont() {
        return "Verdana, sans-serif";
    }
    /**
     * It returns primary, secondary and disabled foreground colors used for text color
     * @return {primary:color, secondary:color2, disabled:color3}
     */
    getForegroundColors() {
        return {
            primary: "#86d486",
            secondary: "#b3b3b3",
            disabled: "#b3b3b3"
        };
    }
    /**
     * It returns primary, secondary and disabled background colors
     * @return {primary:color, secondary:color, disabled:color}
     */
    getBackgroundColors() {
        return {
            primary: "#1a1a1a",
            secondary: "rgb(102, 102, 102, 0.95)",
            disabled: "#f2f2f2"
        };
    }
    /**
     * It returns color used for highlight hovered item
     * @return {color}
     */
    getHoverColor() {
        return "#cffccf";
    }
    /**
     * It returns 4 primary colors
     * monochrom - color used as color scale by changing its intensity
     * lineColor – easy visible color with high contrast
     * triadic1,2,3 - 3 triadic colors scheme (triadic not required but colors contrast recommended)
     * @return {monochrom:color, monochromaComplement:color, triadic1:color, triadic2:color, triadic3:color}
     */
    getDataColors() {
        return {
            lineColor: "#009933",
            triadic1: "#ecec79",
            triadic2: "#e87d7d",
            triadic3: "#a3a3f5",
        };
    }
    /**
     * It returns text input colors
     * matchBg,matchFg- colors for match cases autocomplete
     * notMatchBg, notMatchFg- colors for not matching cases autocomplete
     * placeholder- color of placeholder
     * hover- color when hover object
     * @return {matchBg:color, matchFg:color, notMatchBg:color, notMatchFg:color, placeholder:color, hover:color}
     */
    getTextInputColor() {
        return {
            matchBg: "#8c8c8c",
            matchFg: "#d9d9d9",
            notMatchBg: "#666666",
            notMatchFg: "#b3b3b3",
            placeholder: "#bfbfbf",
            hover: "rgb(0, 51, 0, 0.3)"
        };
    }
}

/**
 * This class defines a custom theme.
 *
 * @author Jiri Hynek
 * @author Jakub Kachlik - advanced color model
 */
class Dark3Theme extends BasicTheme {
    /**
     * It initializes the dark theme.
     */
    constructor() {
        super();
    }
    /**
     * It returns the theme type.
     */
    getName() {
        return "dark3";
    }
    /**
     * This theme prefers dark colors.
     */
    isDark() {
        return true;
    }
    /**
     * It returns the preferred base map.
     */
    getBaseMap() {
        return {
            url: 'https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        };
    }
    /**
     * It returns primary, secondary and disabled foreground colors used for text color
     * @return {primary:color, secondary:color2, disabled:color3}
     */
    getForegroundColors() {
        return {
            primary: "#b3b3b3",
            secondary: "#808080",
            disabled: "#cccccc"
        };
    }
    /**
     * It returns primary, secondary and disabled background colors
     * @return {primary:color, secondary:color, disabled:color}
     */
    getBackgroundColors() {
        return {
            primary: "#666666",
            secondary: "#1a1a1a",
            disabled: "#f2f2f2"
        };
    }
    /**
     * It returns 4 primary colors
     * monochrom - color used as color scale by changing its intensity
     * lineColor – easy visible color with high contrast
     * triadic1,2,3 - 3 triadic colors scheme (triadic not required but colors contrast recommended)
     * @return {monochrom:color, monochromaComplement:color, triadic1:color, triadic2:color, triadic3:color}
     */
    getDataColors() {
        return {
            lineColor: "#c188dd",
            triadic1: "#739550",
            triadic2: "#fff729",
            triadic3: "rgba(255, 0, 0, 0.69)",
        };
    }
    /**
     * It returns text input colors
     * matchBg,matchFg- colors for match cases autocomplete
     * notMatchBg, notMatchFg- colors for not matching cases autocomplete
     * placeholder- color of placeholder
     * hover- color when hover object
     * @return {matchBg:color, matchFg:color, notMatchBg:color, notMatchFg:color, placeholder:color, hover:color}
     */
    getTextInputColor() {
        return {
            matchBg: "#8c8c8c",
            matchFg: "#d9d9d9",
            notMatchBg: "#666666",
            notMatchFg: "#b3b3b3",
            placeholder: "#bfbfbf",
            hover: "rgb(0, 51, 0, 0.3)"
        };
    }
}

/**
 * This class defines a custom theme.
 *
 * @author Jiri Hynek
 * @author Jakub Kachlik - advanced color model
 */
class Light1Theme extends BasicTheme {
    /**
     * It initializes the light theme.
     */
    constructor() {
        super();
    }
    /**
     * It returns the theme type.
     */
    getName() {
        return "light1";
    }
    /**
     * It returns the preferred base map.
     */
    getBaseMap() {
        return {
            url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        };
    }
}

/**
 * This class defines a custom theme.
 *
 * @author Jiri Hynek
 * @author Jakub Kachlik - advanced color model
 */
class Light2Theme extends BasicTheme {
    /**
     * It initializes the light theme.
     */
    constructor() {
        super();
    }
    /**
     * It returns the theme type.
     */
    getName() {
        return "light2";
    }
    /**
     * It returns the preferred base map.
     */
    getBaseMap() {
        return {
            url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        };
    }
    /**
     * It returns theme font
     */
    getFont() {
        return "Georgia, serif";
    }
    /**
     * It returns primary, secondary and disabled foreground colors used for text color
     * @return {primary:color, secondary:color2, disabled:color3}
     */
    getForegroundColors() {
        return {
            primary: "#ffffff",
            secondary: "#1c1c1c",
            disabled: "#cccccc"
        };
    }
    /**
     * It returns primary, secondary and disabled background colors
     * @return {primary:color, secondary:color, disabled:color}
     */
    getBackgroundColors() {
        return {
            primary: "#363636",
            secondary: "rgba(184, 184, 184, 0.82)",
            disabled: "#f2f2f2"
        };
    }
    /**
     * It returns color used for highlight hovered item
     * @return {color}
     */
    getHoverColor() {
        return "rgba(255, 238, 0, 0.37)";
    }
    /**
     * It returns 4 primary colors
     * monochrom - color used as color scale by changing its intensity
     * lineColor – easy visible color with high contrast
     * triadic1,2,3 - 3 triadic colors scheme (triadic not required but colors contrast recommended)
     * @return {monochrom:color, monochromaComplement:color, triadic1:color, triadic2:color, triadic3:color}
     */
    getDataColors() {
        return {
            lineColor: "#000000",
            triadic1: "#000000",
            triadic2: "#7a7a7a",
            triadic3: "#ffffff",
        };
    }
}

/**
 * This class wraps a filter operation.
 *
 * @author Jiri Hynek
 * @author Jakub Kachlik - advanced color model
 */
class Light3Theme extends BasicTheme {
    /**
     * It initializes the light theme.
     */
    constructor() {
        super();
    }
    /**
     * It returns the theme type.
     */
    getName() {
        return "light3";
    }
    /**
     * It returns the preferred base map.
     */
    getBaseMap() {
        return {
            url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        };
    }
    /**
     * It returns theme font
     */
    getFont() {
        return "Georgia, serif";
    }
    /**
     * It returns primary, secondary and disabled foreground colors used for text color
     * @return {primary:color, secondary:color2, disabled:color3}
     */
    getForegroundColors() {
        return {
            primary: "#3e3e3e",
            secondary: "#ffffff",
            disabled: "#cccccc"
        };
    }
    /**
     * It returns primary, secondary and disabled background colors
     * @return {primary:color, secondary:color, disabled:color}
     */
    getBackgroundColors() {
        return {
            primary: "#e8e8e8",
            secondary: "rgba(122, 153, 156, 0.86)",
            disabled: "#f2f2f2"
        };
    }
    /**
     * It returns color used for highlight hovered item
     * @return {color}
     */
    getHoverColor() {
        return "#9df1ff";
    }
    /**
     * It returns 4 primary colors
     * monochrom - color used as color scale by changing its intensity
     * lineColor – easy visible color with high contrast
     * triadic1,2,3 - 3 triadic colors scheme (triadic not required but colors contrast recommended)
     * @return {monochrom:color, monochromaComplement:color, triadic1:color, triadic2:color, triadic3:color}
     */
    getDataColors() {
        return {
            lineColor: "#118993",
            triadic1: "rgba(255, 38, 8, 0.73)",
            triadic2: "rgba(235, 255, 28, 0.74)",
            triadic3: "#ffc341",
        };
    }
}

// Geovisto core
/**
 * This class represents tab fragment for Themes tool.
 *
 * @author Jiri Hynek
 */
class ThemesToolMapForm extends MapObjectForm {
    /**
     * It creates a sidebar fragment with respect to the given props.
     *
     * @param tool
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It returns a HTML content of the sidebar fragment which will be placed in a sidebar tab.
     */
    getContent() {
        if (this.htmlContent == undefined) {
            this.htmlContent = this.createContent();
        }
        return this.htmlContent;
    }
    /**
     * Help function which creates the HTML content.
     */
    createContent() {
        // tab pane
        const htmlContent = document.createElement('div');
        // theme input
        const tool = this.getMapObject();
        const themesManager = tool.getState().getThemesManager();
        const changeTheme = function (e) {
            var _a;
            const newTheme = themesManager.getDomain((_a = e.target) === null || _a === void 0 ? void 0 : _a.value);
            if (newTheme) {
                tool.setTheme(newTheme);
            }
        };
        const themeInput = new LabeledAutocompleteFormInput({ label: "Theme", options: themesManager.getDomainNames(), onChangeAction: changeTheme });
        htmlContent.appendChild(themeInput.create());
        themeInput.setValue(tool.getState().getTheme().getName());
        return htmlContent;
    }
}

// Geovisto core
/**
 * This class provide functions which return the default state values.
 *
 * @author Jiri Hynek
 */
class ThemesToolDefaults extends MapToolDefaults {
    /**
     * It returns the default config.
     */
    getConfig() {
        const config = super.getConfig();
        config.theme = undefined;
        return config;
    }
    /**
     * Only one themes tool should be present in the Geovisto map.
     */
    isSingleton() {
        return true;
    }
    /**
     * It returns a unique string of the tool type.
     */
    getType() {
        return ThemesToolDefaults.TYPE;
    }
    /**
     * It returns the label of the tool.
     */
    getLabel() {
        return "Themes";
    }
    /**
     * It returns the icon of the tool.
     */
    getIcon() {
        return '<i class="fa fa-file"></i>';
    }
    /**
     * It returns default themes manager.
     */
    getThemesManager() {
        return new MapThemesManager([
            new Light1Theme(),
            new Light2Theme(),
            new Light3Theme(),
            new Dark1Theme(),
            new Dark2Theme(),
            new Dark3Theme(),
            new BasicTheme()
        ]);
    }
    /**
     * It returns default theme.
     */
    getTheme(themesManager) {
        if (!themesManager) {
            themesManager = this.getThemesManager();
        }
        let theme = themesManager.getDefault();
        if (theme == undefined) {
            theme = new BasicTheme();
        }
        return theme;
    }
}
/**
 * Static tool type constant.
 */
ThemesToolDefaults.TYPE = "geovisto-tool-themes";

// Geovisto core
/**
 * This class provides the theme change event.
 *
 * @author Jiri Hynek
 */
class ThemesToolEvent extends MapChangeEvent {
    /**
     * It initializes event.
     */
    constructor(themesTool, theme) {
        super(ThemesToolEvent.TYPE(), themesTool, theme);
    }
    /**
     * Type of the event.
     */
    static TYPE() {
        return "themes-tool-event";
    }
}

/**
 * API for Selection Tool.
 *
 * @author Jiri Hynek
 */
const ThemesToolAPI = {
    getType: () => ThemesToolDefaults.TYPE,
    getChangeEventType: () => ThemesToolEvent.TYPE(),
    getTheme: () => undefined,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setTheme: (theme) => { return; }
};

// Geovisto core
/**
 * This class provide functions for using themes.
 *
 * @author Jiri Hynek
 */
class ThemesToolState extends MapToolState {
    /**
     * It creates a tool state.
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It resets the state with respect to the initial props.
     *
     * @param defaults
     * @param props
     * @param initProps
     */
    initialize(defaults, props, initProps) {
        // set theme manager - needs to be set before the theme
        this.setThemesManager(props.manager == undefined ? defaults.getThemesManager() : props.manager);
        // set theme
        this.setTheme(props.theme == undefined ? defaults.getTheme(this.getThemesManager()) : props.theme);
        // initialize super props
        super.initialize(defaults, props, initProps);
    }
    /**
     * The metod takes config and deserializes the values.
     *
     * @param config
     */
    deserialize(config) {
        super.deserialize(config);
        // deserialize theme id
        if (config.theme != undefined) {
            // get filter and data manegers which are need for proper deserialization of filter rules
            const themesManager = this.getThemesManager();
            if (themesManager != undefined) {
                const theme = themesManager.getDomain(config.theme);
                if (theme) {
                    this.setTheme(theme);
                }
            }
        }
    }
    /**
     * The method serializes the tool state. Optionally, defaults can be set if property is undefined.
     *
     * @param defaults
     */
    serialize(defaults) {
        const config = super.serialize(defaults);
        // serialize the theme
        const theme = this.getTheme();
        const defaultTheme = defaults === null || defaults === void 0 ? void 0 : defaults.getTheme(this.getThemesManager());
        config.theme = defaultTheme && theme.getName() == defaultTheme.getName() ? undefined : theme.getName();
        return config;
    }
    /**
     * It returns themes manager.
     */
    getThemesManager() {
        return this.manager;
    }
    /**
     * It sets themes manager.
     *
     * @param manager
     */
    setThemesManager(manager) {
        this.manager = manager;
    }
    /**
     * It returns the theme property of the tool state.
     */
    getTheme() {
        return this.theme;
    }
    /**
     * It sets the theme property of the tool state.
     *
     * @param theme
     */
    setTheme(theme) {
        this.theme = theme;
    }
}

// Geovisto core
/**
 * Attribute which is set to the map container.
 */
const THEME_ATTR_NAME = "data-theme";
/**
 * This class provides the themes tool.
 *
 * @author Jiri Hynek
 */
class ThemesTool extends MapTool {
    /**
     * It creates a new tool with respect to the props.
     *
     * @param props
     */
    constructor(props) {
        super(props);
    }
    /**
     * It creates a copy of the uninitialized tool.
     */
    copy() {
        return new ThemesTool(this.getProps());
    }
    /**
     * It returns the props given by the programmer.
     */
    getProps() {
        return super.getProps();
    }
    /**
     * It returns default values of the themes tool.
     */
    getDefaults() {
        return super.getDefaults();
    }
    /**
     * It creates new defaults of the tool.
     */
    createDefaults() {
        return new ThemesToolDefaults();
    }
    /**
     * It returns the themes tool state.
     */
    getState() {
        return super.getState();
    }
    /**
     * It returns the tool state.
     */
    createState() {
        return new ThemesToolState(this);
    }
    /**
     * It returns the tool API
     */
    getAPIGetter() {
        return {
            getGeovistoThemesTool: () => ThemesTool.api
        };
    }
    /**
     * Overrides the super method.
     *
     * @param initProps
     */
    initialize(initProps) {
        this.initializeAPI();
        return super.initialize(initProps);
    }
    /**
     * Help method which initializes the API.
     */
    initializeAPI() {
        // use this tool reference in API (this tool should be used as a singleton)
        ThemesTool.api.getTheme = () => this.getState().getTheme();
        ThemesTool.api.setTheme = (theme) => this.setTheme(theme);
    }
    /**
     * It creates new filter tool.
     */
    create() {
        // set theme
        const theme = this.getState().getTheme();
        if (theme) {
            this.setTheme(theme);
        }
        return this;
    }
    /**
     * It updates the theme and notifies listeners.
     *
     * @param theme
     */
    setTheme(theme) {
        if (theme != undefined) {
            // if the theme tool is enabled, update map theme
            const map = this.getMap();
            if (this.isEnabled() && map) {
                // update map container theme attribute (used by CSS selectors)
                const mapContainer = document.getElementById(map.getId());
                if (mapContainer) {
                    mapContainer.setAttribute(THEME_ATTR_NAME, theme.getName());
                }
                // update tool state
                this.getState().setTheme(theme);
                // update global styles
                this.updateGlobalStyles(theme);
                // dispatch event
                map.getState().getEventManager().scheduleEvent(new ThemesToolEvent(this, theme), undefined, undefined);
            }
        }
    }
    /**
     * This function updates the global styles.
     *
     * @param theme
     */
    updateGlobalStyles(theme) {
        document.documentElement.style.setProperty('--default-font', theme.getFont());
    }
    /**
     * It returns tab control with respect to the configuration
     */
    getMapForm() {
        if (this.mapForm == undefined) {
            this.mapForm = this.createMapForm();
        }
        return this.mapForm;
    }
    /**
     * It creates new tab fragment.
     */
    createMapForm() {
        // override if needed
        return new ThemesToolMapForm(this);
    }
}
/**
 * tool api
 */
ThemesTool.api = ThemesToolAPI;

const GeovistoThemesTool = Object.assign(Object.assign({}, ThemesToolAPI), { createTool: (props) => new ThemesTool(props), createThemesManager: (themes) => new MapThemesManager(themes), createThemeBasic: () => new BasicTheme(), createThemeLight1: () => new Light1Theme(), createThemeLight2: () => new Light2Theme(), createThemeLight3: () => new Light3Theme(), createThemeDark1: () => new Dark1Theme(), createThemeDark2: () => new Dark2Theme(), createThemeDark3: () => new Dark3Theme() });

export { BasicTheme, Dark1Theme, Dark2Theme, Dark3Theme, GeovistoThemesTool, Light1Theme, Light2Theme, Light3Theme, MapThemesManager, ThemesTool, ThemesToolAPI, ThemesToolDefaults, ThemesToolEvent, ThemesToolMapForm, ThemesToolState };
//# sourceMappingURL=index.es.js.map
